/* eslint-disable react-refresh/only-export-components */
/**
 * HomeTab — Support Hub landing page
 *
 * Greeting with user's name, quick-action cards, and context-aware suggestions.
 */

import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    alpha,
    useTheme,
} from '@mui/material';
import {
    AutoAwesome as AIIcon,
    MenuBook as HelpIcon,
    BugReport as BugIcon,
    Forum as ContactIcon,
    TipsAndUpdates as TipIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useSupportHub } from '../SupportHubContext';
import { useLocation } from 'react-router-dom';

// Map routes to friendly names and suggested questions
export const ROUTE_CONTEXT: Record<string, { label: string; suggestions: string[] }> = {
    '/dashboard/jobs': {
        label: 'Jobs',
        suggestions: ['How do I create a new job?', 'How does job scheduling work?'],
    },
    '/dashboard/compliance': {
        label: 'Compliance',
        suggestions: ['What documents do I need?', 'How do I check my compliance status?'],
    },
    '/dashboard/documents': {
        label: 'Documents',
        suggestions: ['How do I upload a document?', 'What file formats are accepted?'],
    },
    '/dashboard/messages': {
        label: 'Messages',
        suggestions: ['How do notifications work?', 'Can I send attachments?'],
    },
    '/dashboard/profile': {
        label: 'Profile',
        suggestions: ['How do I update my trade types?', 'How do I change my email?'],
    },
    '/dashboard/settings': {
        label: 'Settings',
        suggestions: ['How do I update company details?', 'How do notifications work?'],
    },
    '/dashboard': {
        label: 'Dashboard',
        suggestions: ['How do I read the compliance chart?', 'What does my completion % mean?'],
    },
    '/documents': {
        label: 'Documents',
        suggestions: ['How do I upload a document?', 'What file formats are accepted?'],
    },
    '/jobs': {
        label: 'Jobs',
        suggestions: ['How do I create a new job?', 'How does job scheduling work?'],
    },
    '/compliance': {
        label: 'Compliance',
        suggestions: ['What documents do I need?', 'How do I check my compliance status?'],
    },
    '/profile': {
        label: 'Profile',
        suggestions: ['How do I update my trade types?', 'How do I change my email?'],
    },
    '/messages': {
        label: 'Messages',
        suggestions: ['How do notifications work?', 'Can I send attachments?'],
    },
};

export function matchRouteContext(
    pathname: string,
    routeContextMap: Record<string, { label: string; suggestions: string[] }> = ROUTE_CONTEXT,
): { label: string; suggestions: string[] } | null {
    if (routeContextMap[pathname]) {
        return routeContextMap[pathname];
    }
    // Longest-prefix match on known routes
    const sortedKeys = Object.keys(routeContextMap).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
        if (pathname === key || pathname.startsWith(`${key}/`)) {
            return routeContextMap[key];
        }
    }
    return null;
}

interface QuickAction {
    icon: React.ReactNode;
    label: string;
    description: string;
    tab: 'ai' | 'help' | 'report' | 'contact';
    gradient: string;
}

const QUICK_ACTIONS: QuickAction[] = [
    {
        icon: <AIIcon />,
        label: 'Ask AI',
        description: 'Get instant answers',
        tab: 'ai',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
        icon: <HelpIcon />,
        label: 'Help Center',
        description: 'Browse articles & FAQs',
        tab: 'help',
        gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
        icon: <BugIcon />,
        label: 'Report Issue',
        description: 'Screenshot & annotate',
        tab: 'report',
        gradient: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        icon: <ContactIcon />,
        label: 'Contact Us',
        description: 'Submit a question',
        tab: 'contact',
        gradient: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
    },
];

const HomeTab: React.FC = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const { setActiveTab, askAiQuestion } = useSupportHub();
    const location = useLocation();

    // Determine the current page context using exact or longest-prefix match
    const context = matchRouteContext(location.pathname);

    // User's display name
    const displayName = (user as { user_metadata?: { first_name?: string } } | null)?.user_metadata?.first_name || 'there';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* ── Greeting ── */}
            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"DM Sans", sans-serif', mb: 0.5 }}>
                    Hi {displayName}! 👋
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    How can we help you today?
                </Typography>
            </Box>

            {/* ── Quick Actions Grid ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {QUICK_ACTIONS.map((action) => (
                    <Card
                        key={action.tab}
                        elevation={0}
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '12px',
                            overflow: 'hidden',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.12)}`,
                            },
                        }}
                    >
                        <CardActionArea
                            onClick={() => setActiveTab(action.tab)}
                            sx={{ p: 1.5 }}
                        >
                            <CardContent sx={{ p: '0 !important', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '10px',
                                        background: action.gradient,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                                    }}
                                >
                                    {action.icon}
                                </Box>
                                <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                                    {action.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                                    {action.description}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Box>

            {/* ── Context-Aware Suggestion ── */}
            {context && (
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                        <TipIcon sx={{ fontSize: '1rem', color: 'secondary.main' }} />
                        <Typography variant="caption" fontWeight={600} color="secondary.main">
                            You're on {context.label}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {context.suggestions.map((s) => (
                            <Chip
                                key={s}
                                label={s}
                                size="small"
                                variant="outlined"
                                clickable
                                onClick={() => {
                                    if (askAiQuestion) {
                                        askAiQuestion(s);
                                    } else {
                                        setActiveTab('ai');
                                    }
                                }}
                                sx={{
                                    fontSize: '0.72rem',
                                    height: 26,
                                    borderColor: alpha(theme.palette.secondary.main, 0.3),
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.secondary.main, 0.12),
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default HomeTab;
